import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { lessonsClient, lessonCompletionsClient } from '../apis/client-instances';
import { alert, getFileUrl } from './function';

export function useLessonManagement(courseId: number) {
  const [lessons, setLessons] = useState<any[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<any>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [lessonToEdit, setLessonToEdit] = useState<any>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<any>(null);
  const [completedLessonIds, setCompletedLessonIds] = useState<number[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  // Resolve each lesson's display URL and commit to state. Shared by the
  // standalone fetch and by seedLessons (curriculum arriving inline on the
  // course detail payload) so both paths hydrate file URLs identically.
  //   For lessons authored in our LMS, `file` is a blob name we resolve to a
  //   SAS-signed URL. For imported lessons, the asset lives at an external
  //   public URL stored in `attachedUrl`. getFileUrl returns absolute URLs
  //   as-is, so the fallback works for both cases.
  const hydrateLessons = async (les: any[]) => {
    const updatedLessons = await Promise.all(
      les.map(async (lesson) => ({
        ...lesson,
        fileUrl: await getFileUrl(lesson.file || lesson.attachedUrl),
      }))
    );
    setLessons(updatedLessons);
  };

  const fetchLessons = async () => {
    // Skip fetch if courseId is invalid (0 or negative)
    if (!courseId || courseId <= 0) {
      return;
    }
    try {
      const les = await lessonsClient.getLessonsByCourse(courseId);
      if (les?.length) {
        await hydrateLessons(les);
        // Initial selection is owned by CourseDetail (needs module order to pick the
        // first lesson of the first module — lesson SequenceOrder resets per module).
      }
    } catch (error) {
      console.error('Failed to fetch lessons:', error);
    }
  };

  // Seed lessons from the course detail payload (course.lessons) so the player
  // doesn't fire a second round trip that races the module fetch. Empty arrays
  // pass through — a course can legitimately have no lessons yet.
  const seedLessons = async (les: any[]) => {
    await hydrateLessons(les ?? []);
  };

  const openAddDialog = () => {
    setLessonToEdit(null);
    setShowDialog(true);
  };

  // Same as openAddDialog but pre-fills the module dropdown. Used by the
  // curriculum builder's per-module "+ Add lesson" so admins don't have
  // to re-pick the module they're already standing in.
  const openAddDialogForModule = (moduleId: number) => {
    setLessonToEdit({ moduleId } as any);
    setShowDialog(true);
  };

  const openEditDialog = (lesson: any) => {
    setLessonToEdit(lesson);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setLessonToEdit(null);
  };

  /**
   * Upload a SCORM package zip to an existing lesson. The endpoint unzips,
   * parses imsmanifest.xml, uploads to public blob, and writes the three
   * SCORM addressing fields onto the row. Returns the populated fields so
   * the caller can patch the local lesson state without refetching.
   *
   * Uses direct fetch rather than the generated client because multipart
   * upload is simpler as vanilla FormData. Auth pattern matches ApiBase.
   */
  const uploadScormZipToLesson = async (lessonId: number, zip: File) => {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://localhost:44313';
    const form = new FormData();
    form.append('file', zip);
    const resp = await fetch(`${baseUrl}/api/Lessons/${lessonId}/scorm-upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || `SCORM upload failed (${resp.status})`);
    }
    return await resp.json() as {
      lessonId: number;
      packageGuid: string;
      packageUrl: string;
      entryPoint: string;
      fileCount: number;
    };
  };

  const saveLesson = async (lessonItem: any) => {
    // EditLesson packs the SCORM zip onto the lesson item as a File. We
    // strip it before sending to the JSON API, upload it separately after
    // the row exists, then patch the returned fields back onto the local
    // lesson object so selectedLesson can render immediately.
    const scormZip = lessonItem.scormZip as File | undefined;
    if (scormZip !== undefined) {
      delete lessonItem.scormZip;
    }

    try {
      let savedLesson: any;

      if (lessonToEdit) {
        await lessonsClient.updateLesson(lessonToEdit.id, lessonItem);
        // Preserve fields not included in lessonItem (like the existing scorm
        // fields on a metadata-only edit of a SCORM lesson).
        savedLesson = { ...lessonToEdit, ...lessonItem, id: lessonToEdit.id };
      } else {
        const result = await lessonsClient.addLesson(lessonItem, courseId);
        savedLesson = { ...lessonItem, id: result.id };
      }

      // SCORM post-save: upload the zip and patch the returned fields into
      // the local lesson so ScormViewer can immediately play it.
      if (scormZip) {
        const scormResult = await uploadScormZipToLesson(savedLesson.id, scormZip);
        savedLesson.scormPackageUrl = scormResult.packageUrl;
        savedLesson.scormEntryPoint = scormResult.entryPoint;
        savedLesson.scormPackageGuid = scormResult.packageGuid;
      }

      if (lessonToEdit) {
        setLessons((prev) => prev.map((l) => (l.id === savedLesson.id ? savedLesson : l)));
        if (selectedLesson?.id === savedLesson.id) {
          setSelectedLesson(savedLesson);
        }
        setLessonToEdit(null);
      } else {
        setLessons((prev) => [...prev, savedLesson]);
        // Always navigate to the newly created lesson — admin just authored
        // it, they want to see it. Previously this was guarded by
        // `!selectedLesson`, which made new lessons effectively invisible on
        // any course that already had content.
        setSelectedLesson(savedLesson);
      }

      alert(enqueueSnackbar, 'Lesson saved successfully', 'success');
      return savedLesson;
    } catch (error) {
      alert(enqueueSnackbar, 'Save failed', 'error');
      throw error;
    }
  };

  /**
   * Persist per-lesson rule fields (gate / completion criteria) without
   * opening the full editor. The API's update endpoint takes the whole
   * AddLessonRequest, so this is a read-modify-write: rebuild the request
   * from the loaded lesson (mirroring the fields EditLesson sends) and
   * override only the rule fields being changed. Optimistically patches
   * local state so the row reflects the change immediately.
   */
  const updateLessonRules = async (
    lessonId: number,
    patch: { isGated?: boolean; isRequired?: boolean; minWatchPercent?: number; quizSettings?: any },
  ) => {
    const existing = lessons.find((l) => l.id === lessonId);
    if (!existing) return;
    const request = {
      accountId: existing.accountId,
      title: existing.title,
      lessonType: existing.lessonType,
      description: existing.description,
      thumbnail: existing.thumbnail,
      attachedUrl: existing.attachedUrl,
      file: existing.file,
      textContent: existing.textContent,
      moduleId: existing.moduleId,
      videoProvider: existing.videoProvider,
      externalVideoId: existing.externalVideoId,
      quizSettings: existing.quizSettings,
      isRequired: existing.isRequired,
      minWatchPercent: existing.minWatchPercent,
      isGated: existing.isGated,
      ...patch,
    };
    const updated = { ...existing, ...patch };
    // Optimistic — revert by refetching on failure.
    setLessons((prev) => prev.map((l) => (l.id === lessonId ? updated : l)));
    if (selectedLesson?.id === lessonId) setSelectedLesson(updated);
    try {
      await lessonsClient.updateLesson(lessonId, request as any);
    } catch (error) {
      console.error('Failed to save lesson settings:', error);
      alert(enqueueSnackbar, 'Failed to save lesson settings', 'error');
      fetchLessons();
      throw error;
    }
  };

  const confirmDelete = (lessonId: number) => {
    setDeleteId(lessonId);
    setConfirmOpen(true);
  };

  const deleteLesson = async () => {
    setConfirmOpen(false);
    if (deleteId) {
      try {
        await lessonsClient.deleteLesson(deleteId);
        setLessons((prev) => prev.filter((lesson) => lesson.id !== deleteId));
        setSelectedLesson(lessons.filter((lesson) => lesson.id !== deleteId)?.[0]);
      } catch (error) {
        console.error('Failed to delete lesson:', error);
        alert(enqueueSnackbar, 'Failed to delete lesson', 'error');
      }
    }
    setDeleteId(null);
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const fetchCompletedLessons = async (enrollmentId: number) => {
    try {
      const completions = await lessonCompletionsClient.getEnrollmentCompletions(enrollmentId);
      const completedIds = completions.map((c: any) => c.lessonId).filter((id: any) => id != null);
      setCompletedLessonIds(completedIds);
    } catch (error) {
      console.error('Failed to fetch lesson completions:', error);
      setCompletedLessonIds([]);
    }
  };

  const reorderLessons = async (lessonIdsInOrder: number[]) => {
    try {
      await lessonsClient.reorderLessons(courseId, lessonIdsInOrder);
      // Optimistically update local state
      const reorderedLessons = lessonIdsInOrder
        .map((id) => lessons.find((l) => l.id === id))
        .filter((l) => l != null);
      setLessons(reorderedLessons);
      alert(enqueueSnackbar, 'Lessons reordered successfully', 'success');
    } catch (error) {
      console.error('Failed to reorder lessons:', error);
      alert(enqueueSnackbar, 'Failed to reorder lessons', 'error');
      // Refresh lessons to revert optimistic update
      fetchLessons();
    }
  };

  return {
    lessons,
    selectedLesson,
    setSelectedLesson,
    fetchLessons,
    seedLessons,
    // Completion tracking
    completedLessonIds,
    fetchCompletedLessons,
    // Lesson reordering
    reorderLessons,
    // Dialog state
    showDialog,
    lessonToEdit,
    openAddDialog,
    openAddDialogForModule,
    openEditDialog,
    closeDialog,
    saveLesson,
    updateLessonRules,
    // Confirm dialog state
    confirmOpen,
    confirmDelete,
    deleteLesson,
    cancelDelete,
  };
}
